import { useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { StatusBadge, SeverityBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../context/AuthContext";

const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];
const STATUSES = ["NEW", "ACKNOWLEDGED", "IN_PROGRESS", "RESOLVED", "OVERRIDDEN", "REOPENED"];
const CATEGORIES = ["SCHEDULE_MILESTONE", "SCOPE_REQUIREMENT", "DEPENDENCY", "TECHNICAL", "QUALITY_TESTING",
  "SECURITY_COMPLIANCE", "RESOURCE_CAPACITY", "COMMUNICATION_DECISION", "OPERATIONAL_RELEASE",
  "COMMERCIAL_EXTERNAL", "TEAM_ASSISTANCE", "REPORTING_VISIBILITY"];

function FilterSelect({ value, onChange, options, placeholder, testId }) {
  const { t } = useLang();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-40" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="ALL">{t("all")}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function WarningDetail({ warning, onChanged, canAct }) {
  const { t } = useLang();
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideSeverity, setOverrideSeverity] = useState("KEEP");
  const [intOpen, setIntOpen] = useState(false);
  const [intAction, setIntAction] = useState("");
  const [intDate, setIntDate] = useState("");
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState("");

  const load = useCallback(() => {
    api.get(`/warnings/${warning.id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [warning.id]);
  useEffect(() => {
    load();
    if (canAct) api.get("/users/options").then((r) => setUsers(r.data)).catch(() => {});
  }, [load, canAct]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!detail) return <Skeleton className="h-20 w-full mt-3" />;
  const w = detail.warning;

  return (
    <div className="mt-3 pt-3 border-t space-y-3">
      {w.evidence?.length > 0 && (
        <div>
          <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1">{t("evidence")}</div>
          {w.evidence.map((ev) => (
            <div key={`${ev.source_id}-${ev.fact}`} className="text-xs text-muted-foreground">• <span className="font-medium text-foreground">{ev.source_id}</span> — {ev.fact}</div>
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground space-x-3">
        <span>{t("source")}: {w.rule_id || w.source}</span>
        {w.owner_name && <span>{t("owner")}: {w.owner_name}</span>}
        {w.acknowledgement_due_at && <span>SLA: {new Date(w.acknowledgement_due_at).toLocaleString()}</span>}
        {w.override_reason && <span className="text-[#9A3412]">{t("override")}: {w.override_reason}</span>}
      </div>

      {detail.interventions.length > 0 && (
        <div>
          <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1">{t("intervention")}</div>
          {detail.interventions.map((i) => (
            <div key={i.id} className="text-xs flex items-center gap-2 py-0.5">
              <span>{i.action}</span>
              <span className="text-muted-foreground">— {i.owner_name} · {(i.target_at || "").slice(0, 10)} · {i.status}</span>
              {canAct && i.status === "OPEN" && (
                <button className="text-[#1E3A8A] hover:underline" data-testid={`warn-complete-int-${i.id}`}
                  onClick={() => act(() => api.post(`/interventions/${i.id}/complete`), t("complete"))}>{t("complete")}</button>
              )}
            </div>
          ))}
        </div>
      )}

      {detail.comments.length > 0 && (
        <div>
          <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-1">{t("comment")}</div>
          {detail.comments.map((c) => (
            <div key={c.id} className="text-xs py-0.5"><span className="font-medium">{c.author_name}</span>: {c.body}</div>
          ))}
        </div>
      )}

      {canAct && (
        <>
          <div className="flex gap-2">
            <Input data-testid={`warn-comment-input-${w.id}`} placeholder={t("comment") + "..."} value={comment}
              onChange={(e) => setComment(e.target.value)} className="h-8 text-sm" />
            <Button size="sm" variant="outline" data-testid={`warn-comment-btn-${w.id}`} disabled={!comment}
              onClick={() => act(() => api.post(`/warnings/${w.id}/comment`, { body: comment }).then(() => setComment("")), t("comment"))}>
              {t("comment")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {w.status === "NEW" && (
              <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1c3479]" data-testid={`warn-ack-${w.id}`}
                onClick={() => act(() => api.post(`/warnings/${w.id}/acknowledge`), t("acknowledge"))}>{t("acknowledge")}</Button>
            )}
            <Select value={assignee} onValueChange={(v) => { setAssignee(v); act(() => api.post(`/warnings/${w.id}/assign`, { owner_user_id: v }), t("assign")); }}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`warn-assign-select-${w.id}`}>
                <SelectValue placeholder={t("assign")} />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" data-testid={`warn-intervention-${w.id}`} onClick={() => setIntOpen(true)}>{t("addIntervention")}</Button>
            {["NEW", "ACKNOWLEDGED", "IN_PROGRESS", "REOPENED"].includes(w.status) && (
              <>
                <Button size="sm" variant="outline" data-testid={`warn-resolve-${w.id}`}
                  onClick={() => act(() => api.post(`/warnings/${w.id}/resolve`), t("resolve"))}>{t("resolve")}</Button>
                <Button size="sm" variant="outline" data-testid={`warn-escalate-${w.id}`}
                  onClick={() => act(() => api.post(`/warnings/${w.id}/escalate`), t("escalate"))}>{t("escalate")}</Button>
                <Button size="sm" variant="ghost" data-testid={`warn-override-${w.id}`} onClick={() => setOverrideOpen(true)}>{t("override")}</Button>
              </>
            )}
            {["RESOLVED", "OVERRIDDEN"].includes(w.status) && (
              <Button size="sm" variant="outline" data-testid={`warn-reopen-${w.id}`}
                onClick={() => act(() => api.post(`/warnings/${w.id}/reopen`), t("reopen"))}>{t("reopen")}</Button>
            )}
          </div>
        </>
      )}

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{t("override")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("severityLabel")}</Label>
              <Select value={overrideSeverity} onValueChange={setOverrideSeverity}>
                <SelectTrigger data-testid="override-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="KEEP">False positive (close)</SelectItem>
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Reason</Label>
              <Input data-testid="override-reason-input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
            </div>
            <Button data-testid="override-confirm-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={!overrideReason}
              onClick={() => { act(() => api.post(`/warnings/${w.id}/override`, { reason: overrideReason, severity: overrideSeverity === "KEEP" ? null : overrideSeverity }), t("override")); setOverrideOpen(false); }}>
              {t("override")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={intOpen} onOpenChange={setIntOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{t("addIntervention")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("interventionAction")}</Label>
              <Input data-testid="intervention-action-input" value={intAction} onChange={(e) => setIntAction(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>{t("targetDate")}</Label>
              <Input type="date" data-testid="intervention-date-input" value={intDate} onChange={(e) => setIntDate(e.target.value)} />
            </div>
            <Button data-testid="intervention-confirm-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={!intAction}
              onClick={() => { act(() => api.post(`/warnings/${w.id}/interventions`, { action: intAction, target_at: intDate ? `${intDate}T17:00:00+07:00` : null }), t("addIntervention")); setIntOpen(false); setIntAction(""); }}>
              {t("addIntervention")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Warnings() {
  const { t } = useLang();
  const { user } = useAuth();
  const canAct = ["PM", "LEAD", "ADMIN"].includes(user.role);
  const [warnings, setWarnings] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ severity: "ALL", status: "ALL", category: "ALL" });

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "ALL") params[k] = v; });
    api.get("/warnings", { params }).then((r) => setWarnings(r.data)).catch((e) => toast.error(apiError(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="warnings-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("warnings")}</h1>
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-muted-foreground">{t("filters")}:</span>
        <FilterSelect value={filters.severity} onChange={(v) => setFilters({ ...filters, severity: v })}
          options={SEVERITIES} placeholder={t("severityLabel")} testId="filter-severity" />
        <FilterSelect value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
          options={STATUSES} placeholder={t("statusLabel")} testId="filter-status" />
        <FilterSelect value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })}
          options={CATEGORIES} placeholder={t("categoryLabel")} testId="filter-category" />
      </div>
      {!warnings ? (
        <Skeleton className="h-60 w-full" />
      ) : (
        <div className="space-y-3">
          {warnings.map((w) => (
            <div key={w.id} className="apha-card p-4" data-testid={`warning-card-${w.id}`}>
              <button className="w-full text-left" data-testid={`warning-expand-${w.id}`}
                onClick={() => setExpanded(expanded === w.id ? null : w.id)}>
                <div className="flex flex-wrap items-center gap-2">
                  <SeverityBadge severity={w.severity} />
                  <StatusBadge status={w.status} />
                  {w.project_key && <span className="text-xs font-medium bg-secondary rounded px-1.5 py-0.5">{w.project_key}</span>}
                  {w.person_name && <span className="text-xs text-muted-foreground">{w.person_name}</span>}
                  <span className="text-xs text-muted-foreground">{w.category}</span>
                  <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                    {Math.round(w.age_hours || 0)}h {expanded === w.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </span>
                </div>
                <div className="text-sm mt-1.5 font-medium">{w.summary}</div>
              </button>
              {expanded === w.id && <WarningDetail warning={w} onChanged={load} canAct={canAct} />}
            </div>
          ))}
          {warnings.length === 0 && <div className="apha-card p-8 text-sm text-muted-foreground">{t("noData")}</div>}
        </div>
      )}
    </div>
  );
}
